import React, { useCallback, useMemo } from 'react';

import { TextFieldProps } from '@mui/material/TextField';

import { TextField } from './text-field';

// ─── Mask engine ─────────────────────────────────────────────────────────────

type InputRule = { test: (c: string) => boolean; required: boolean };

type MaskToken =
  | { type: 'input'; rule: InputRule }
  | { type: 'literal'; char: string };

const MASK_CHARS: Record<string, InputRule> = {
  '0': { test: (c) => /[0-9]/.test(c), required: true },
  '9': { test: (c) => /[0-9 ]/.test(c), required: false },
  L: { test: (c) => /[A-Za-z]/.test(c), required: true },
  '?': { test: (c) => /[A-Za-z]/.test(c), required: false },
  A: { test: (c) => /[A-Za-z0-9]/.test(c), required: true },
  a: { test: (c) => /[A-Za-z0-9]/.test(c), required: false },
  '&': { test: () => true, required: true },
  C: { test: () => true, required: false },
};

export const parseMask = (mask: string): MaskToken[] => {
  const tokens: MaskToken[] = [];
  let i = 0;
  while (i < mask.length) {
    if (mask[i] === '\\' && i + 1 < mask.length) {
      tokens.push({ type: 'literal', char: mask[i + 1] });
      i += 2;
    } else if (MASK_CHARS[mask[i]]) {
      tokens.push({ type: 'input', rule: MASK_CHARS[mask[i]] });
      i++;
    } else {
      tokens.push({ type: 'literal', char: mask[i] });
      i++;
    }
  }
  return tokens;
};

export const applyMask = (rawValue: string, tokens: MaskToken[]): string => {
  let result = '';
  let rawIndex = 0;
  for (const token of tokens) {
    if (token.type === 'literal') {
      result += token.char;
      if (rawValue[rawIndex] === token.char) rawIndex++;
    } else {
      let placed = false;
      while (rawIndex < rawValue.length) {
        const c = rawValue[rawIndex];
        rawIndex++;
        if (token.rule.test(c)) {
          result += c;
          placed = true;
          break;
        }
      }
      if (!placed && token.rule.required) break;
    }
  }
  return result;
};

export const getMaskPlaceholder = (tokens: MaskToken[]): string =>
  tokens.map((t) => (t.type === 'literal' ? t.char : '_')).join('');

// ─── Component ────────────────────────────────────────────────────────────────

export type MaskedTextFieldProps = Omit<
  TextFieldProps,
  'onChange' | 'value' | 'placeholder'
> & {
  mask: string;
  value?: string;
  onChange?: (maskedValue: string) => void;
};

export const MaskedTextField = ({
  mask,
  value,
  onChange,
  ...rest
}: MaskedTextFieldProps) => {
  const tokens = useMemo(() => parseMask(mask), [mask]);
  const placeholder = useMemo(() => getMaskPlaceholder(tokens), [tokens]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(applyMask(e.target.value, tokens));
    },
    [onChange, tokens]
  );

  return (
    <TextField
      {...rest}
      value={value ?? ''}
      placeholder={placeholder}
      onChange={handleChange}
    />
  );
};
